import { useAuth0, User } from "@auth0/auth0-react";
import { useCreateTractor } from "./api";
import type { TractorFormData } from "./tractors";
import Button from "components/button";
import Input from "components/input/input";
import type { ChangeEvent, Dispatch, FormEvent, SetStateAction } from "react";

interface FormProps {
  tractorData: TractorFormData;
  setTractorData: Dispatch<SetStateAction<TractorFormData>>;
  resetTractorData: () => void;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

export default function Form({ tractorData, setTractorData, resetTractorData, setIsModalOpen }: FormProps) {
  const auth = useAuth0();
  const user = auth.user as Exclude<User, undefined>;

  const saveTractor = useCreateTractor();
  const isLoading = saveTractor.isPending;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    saveTractor.mutate(
      {
        customer: user["https://ifuelsmart.com/company"] as string,
        tractor: {
          unit: tractorData.unit_number,
          email_address: tractorData.email_address,
          tank_capacity: Number(tractorData.tank_capacity),
          status: tractorData.status,
          id: tractorData.id,
          MPG: Number(tractorData.MPG),
        },
      },
      {
        onSuccess: () => {
          resetTractorData();
          setIsModalOpen(false);
        },
      }
    );
  }

  function handleModalClose() {
    resetTractorData();
    setIsModalOpen(false);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const { name } = e.target;
    if (e.target.type === "checkbox") {
      const { checked } = e.target;
      setTractorData((prevState) => ({ ...prevState, [name]: checked }));
    } else {
      const { value } = e.target;
      setTractorData((prevState) => ({ ...prevState, [name]: value }));
    }
  }

  return (
    <>
      <div className="absolute bg-[rgba(0,0,0,0.5)] inset-0 z-10 grid place-items-center">
        <div className="bg-white w-full max-w-sm p-5 shadow-lg rounded-sm">
          <form onSubmit={handleSubmit}>
            <div className="flex justify-between items-center mb-4">
              <div className="font-semibold text-lg">{tractorData.id ? "Edit Tractor" : "Create Tractor"}</div>
              <button type="button" className="font-medium text-xl cursor-pointer" onClick={handleModalClose}>
                x
              </button>
            </div>
            <Input
              label="Unit Number"
              type="text"
              placeholder="Unit Number"
              name="unit_number"
              value={tractorData.unit_number}
              onChange={handleInputChange}
              required
            />
            <Input
              label="Email / Driver Id"
              type="text"
              placeholder="Email / Driver Id"
              name="email_address"
              value={tractorData.email_address}
              onChange={handleInputChange}
            />
            <Input
              label="Tank Capacity"
              type="text"
              placeholder="Tank Capacity"
              name="tank_capacity"
              value={tractorData.tank_capacity}
              onChange={handleInputChange}
              required
            />
            <Input
              label="Tractor MPG"
              type="text"
              placeholder="Enter Tractor MPG"
              name="MPG"
              value={tractorData.MPG}
              onChange={handleInputChange}
              required
            />
            <Input
              label="Status"
              type="checkbox"
              placeholder="Status"
              className="!w-min"
              name="status"
              checked={tractorData.status}
              onChange={handleInputChange}
            />
            <Button type="submit">
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Processing
                </>
              ) : tractorData.id ? (
                "Update Tractor"
              ) : (
                "Create Tractor"
              )}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}
